import { useRef, useState, useEffect, useMemo } from "react";
import { Formik, Field, Form } from "formik";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  Wrapper,
  ActionSection,
  ScrollableContentX,
  Note,
  RupeeIcon,
} from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import Config, { paidByOptions, paidByRent } from "../../Config";

const TrackCommission = ({
  handleClose,
  key,
  selectedData,
  trackCommissionLoading,
  trackCommissionMutate,
}) => {
  const formikRef = useRef(null);
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const validationSchema = Yup.object()
    .shape({
      buyerAmount: Yup.number()
        .typeError("Enter valid amount")
        .min(0, "Cannot be negative")
        .test(
          "buyer-max",
          "Amount cannot be greater than pending",
          function (value) {
            const buyerBalance = Number(selectedData?.buyerBalance || 0);

            if (!value) return true; // not required
            return value <= buyerBalance;
          },
        ),

      sellerAmount: Yup.number()
        .typeError("Enter valid amount")
        .min(0, "Cannot be negative")
        .test(
          "seller-max",
          "Amount cannot be greater than pending",
          function (value) {
            const sellerBalance = Number(selectedData?.sellerBalance || 0);

            if (!value) return true; // not required
            return value <= sellerBalance;
          },
        ),

      // note: Yup.string().max(200, "Max 200 characters"),
    })
    .test(
      "at-least-one",
      "Enter at least one amount (Buyer or Seller)",
      function (values) {
        const buyer = Number(values.buyerAmount || 0);
        const seller = Number(values.sellerAmount || 0);

        return buyer > 0 || seller > 0;
      },
    );

  const initialValues = {
    balance: selectedData?.balance || 0,
    commissionPaidBy:
      selectedData?.commissionPaidBy || Config.PAID_BY_TYPE.BUYER,
    buyerAmount: "",
    sellerAmount: "",
    note: selectedData?.note || "",
    amount: selectedData?.amount || 0,
  };

  const handleSubmit = (values) => {
    const payload = {
      dealId: selectedData?.dealId,
      buyerAmount: Number(values.buyerAmount || 0),
      sellerAmount: Number(values.sellerAmount || 0),
      commissionPaidBy: values.commissionPaidBy,
      note: values.note,
    };
    trackCommissionMutate(payload);
    // handleClose();
  };

  const isRental = selectedData?.listingType === 1;

  const {
    buyerLabel,
    sellerLabel,
    buyerPendingLabel,
    sellerPendingLabel,
    paidByList,
  } = useMemo(() => {
    if (isRental) {
      return {
        buyerLabel: "Tenant Amount",
        sellerLabel: "Owner Amount",
        buyerPendingLabel: "Pending Tenant",
        sellerPendingLabel: "Pending Owner",
        paidByList: paidByRent,
      };
    }

    return {
      buyerLabel: "Buyer Amount",
      sellerLabel: "Seller Amount",
      buyerPendingLabel: "Pending Buyer",
      sellerPendingLabel: "Pending Seller",
      paidByList: paidByOptions,
    };
  }, [isRental]);

  return (
    <Container style={{ height: `${height}px` }}>
      <Header>
        <h1 className="text-white text-lg font-medium">Track Commission</h1>
        <button onClick={handleClose}>
          <MdClose color="white" size={24} />
        </button>
      </Header>

      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        context={{
          buyerBalance: selectedData?.buyerBalance,
          sellerBalance: selectedData?.sellerBalance,
        }}
      >
        {({ values, setFieldValue, handleChange, errors, touched }) => {
          const buyerPending = Number(selectedData?.buyerBalance || 0);
          const sellerPending = Number(selectedData?.sellerBalance || 0);

          const buyerPaidNow = Number(values.buyerAmount || 0);
          const sellerPaidNow = Number(values.sellerAmount || 0);

          const buyerRemaining = buyerPending - buyerPaidNow;
          const sellerRemaining = sellerPending - sellerPaidNow;

          const remaining =
            Math.max(buyerRemaining, 0) + Math.max(sellerRemaining, 0);

          const totalAmount = Number(selectedData?.amount || 0);

          const totalPending =
            Number(selectedData?.buyerBalance || 0) +
            Number(selectedData?.sellerBalance || 0);

          const received = Math.max(0, totalAmount - totalPending);

          const progress = totalAmount ? (received / totalAmount) * 100 : 0;

          return (
            <Form className="px-6 h-full flex flex-col justify-between">
              <ScrollableContentX>
                <Wrapper>
                  <div className="bg-slate-50 p-4 rounded-xl">
                    <div className="flex justify-between text-xs mb-2">
                      <span>Total ₹{selectedData?.amount}</span>
                      <span className="text-green-600 font-semibold">
                        Received ₹{received}
                      </span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      />
                    </div>

                    <p className="text-xs text-gray-500 mt-2">
                      Pending ₹{remaining < 0 ? 0 : remaining}
                    </p>
                  </div>

                  {/* Paid By */}
                  <InputGroup>
                    <Label>Paid By</Label>
                    <div className="flex gap-2">
                      {paidByList.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() =>
                            setFieldValue("commissionPaidBy", option.value)
                          }
                          className={`px-4 py-2 rounded-full border text-sm ${
                            values.commissionPaidBy === option.value
                              ? "bg-primary-color text-white"
                              : "bg-white text-gray-600"
                          }`}
                        >
                          {option.label.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </InputGroup>

                  {(values.commissionPaidBy === Config.PAID_BY_TYPE.BUYER ||
                    values.commissionPaidBy === Config.PAID_BY_TYPE.BOTH) && (
                    <InputGroup>
                      <Label>{buyerLabel}</Label>
                      <FieldWrappers>
                        <RupeeIcon />
                        <Field
                          name="buyerAmount"
                          type="number"
                          className="pl-7 w-full"
                          onChange={handleChange}
                        />
                      </FieldWrappers>

                      <Note>
                        {buyerPendingLabel} ₹{selectedData?.buyerBalance}
                      </Note>
                      {errors.buyerAmount && touched.buyerAmount && (
                        <ErrorText>{errors.buyerAmount}</ErrorText>
                      )}
                    </InputGroup>
                  )}

                  {(values.commissionPaidBy === Config.PAID_BY_TYPE.SELLER ||
                    values.commissionPaidBy === Config.PAID_BY_TYPE.BOTH) && (
                    <InputGroup>
                      <Label>{sellerLabel}</Label>
                      <FieldWrappers>
                        <RupeeIcon />
                        <Field
                          name="sellerAmount"
                          type="number"
                          className="pl-7 w-full"
                          onChange={handleChange}
                        />
                      </FieldWrappers>

                      <Note>
                        {sellerPendingLabel} ₹{selectedData?.sellerBalance}
                      </Note>
                      {errors.sellerAmount && touched.sellerAmount && (
                        <ErrorText>{errors.sellerAmount}</ErrorText>
                      )}
                    </InputGroup>
                  )}

                  {/* Notes */}
                  <InputGroup>
                    <Label>Notes</Label>
                    <Textarea
                      name="note"
                      value={values.note}
                      onChange={handleChange}
                    />
                    {errors.note && touched.note && (
                      <ErrorText>{errors.note}</ErrorText>
                    )}
                  </InputGroup>
                </Wrapper>
              </ScrollableContentX>

              <ActionSection>
                <SideCancelBtn onClick={handleClose}>Cancel</SideCancelBtn>
                <SubmitBtn type="submit">
                  {trackCommissionLoading ? "Saving..." : "Save"}
                </SubmitBtn>
              </ActionSection>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

// Styled Components
const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Header = tw.div`flex items-center justify-between bg-gradient-to-r from-primary-color to-teal-400 py-4 px-4`;
const Textarea = tw.textarea`border border-gray-300 rounded p-2 w-full resize-none`;
const ErrorText = tw.div`text-red-500 text-sm mt-1`;

export default TrackCommission;
